'use client'

import React from 'react'
import {
  Eye,
  Brain,
  Database,
  Zap,
  Shield,
  SlidersHorizontal,
  FileSearch,
  Activity,
  RotateCcw,
  ArrowUpRight,
  Layers,
  MessageSquare,
  Network,
  Monitor,
  Palette,
  Bot,
  Users,
  TrendingUp,
  AlertTriangle,
  Wrench,
} from 'lucide-react'

/**
 * Agentic UX Diagrams
 * Visual components for the "From UX to AX" blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Four Pillars of AX: Perception, Reasoning, Memory, Agency
 */
export function AXCapabilitiesDiagram() {
  const pillars = [
    {
      icon: Eye,
      title: 'Perception',
      color: '#008dcc',
      description: 'What the agent "sees"',
      designImplication:
        'Show users what data and signals the agent is reading. Make inputs visible, not invisible.',
      examples: [
        'Document context indicators',
        'Active tool badges',
        'User state awareness panels',
      ],
    },
    {
      icon: Brain,
      title: 'Reasoning',
      color: '#8b5cf6',
      description: 'How the agent "thinks"',
      designImplication:
        'Reveal hypotheses, plans, and next steps at the right detail level for the user role.',
      examples: [
        'Thinking/reasoning panels',
        'Step-by-step plan previews',
        'Confidence indicators',
      ],
    },
    {
      icon: Database,
      title: 'Memory',
      color: '#FDC304',
      description: 'What the agent "remembers"',
      designImplication:
        'Make long-term memory visible and editable. Users need to correct, update, or forget.',
      examples: [
        'Preference settings surfaces',
        'Past decision audit logs',
        'Editable context panels',
      ],
    },
    {
      icon: Zap,
      title: 'Agency',
      color: '#F90B8A',
      description: 'What the agent "does"',
      designImplication:
        'Distinguish proposed, scheduled, in-progress, and completed actions clearly.',
      examples: [
        'Action status timelines',
        'Approval workflow cards',
        'Undo/rollback controls',
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#8b5cf6]/10 border border-[#8b5cf6]/20 mb-3'>
            <Layers className='w-4 h-4' style={{ color: '#8b5cf6' }} />
            <span
              className='text-sm font-semibold'
              style={{ color: '#8b5cf6' }}
            >
              The AX Framework
            </span>
          </div>
          <h3 className='text-xl font-bold text-foreground'>
            Four Capabilities That Define Agentic Experience
          </h3>
          <p className='text-muted-foreground text-sm mt-2'>
            Each capability requires dedicated UX surfaces, not just model
            configuration
          </p>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {pillars.map(pillar => {
            const Icon = pillar.icon
            return (
              <div
                key={pillar.title}
                className='bg-background border border-border/60 rounded-xl p-5'
              >
                <div className='flex items-center gap-3 mb-3'>
                  <div
                    className='w-10 h-10 rounded-lg flex items-center justify-center'
                    style={{ backgroundColor: `${pillar.color}15` }}
                  >
                    <Icon className='w-5 h-5' style={{ color: pillar.color }} />
                  </div>
                  <div>
                    <h4
                      className='font-bold text-base'
                      style={{ color: pillar.color }}
                    >
                      {pillar.title}
                    </h4>
                    <p className='text-xs text-muted-foreground'>
                      {pillar.description}
                    </p>
                  </div>
                </div>
                <p className='text-sm text-foreground/80 mb-3'>
                  {pillar.designImplication}
                </p>
                <div className='space-y-1.5'>
                  {pillar.examples.map(ex => (
                    <div key={ex} className='flex items-center gap-2 text-xs'>
                      <div
                        className='w-1.5 h-1.5 rounded-full flex-shrink-0'
                        style={{ backgroundColor: pillar.color }}
                      />
                      <span className='text-muted-foreground'>{ex}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-6 bg-[#008dcc]/5 border border-[#008dcc]/20 rounded-xl p-4'>
          <p className='text-sm text-foreground/80 text-center'>
            <strong className='text-foreground'>The shift:</strong> In
            traditional UX, these were invisible backend concerns. In AX, each
            one gets a dedicated design surface that users can inspect and
            control.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Six Agentic UX Patterns Lifecycle
 */
export function AgenticPatternLifecycleDiagram() {
  const phases = [
    {
      phase: 'Pre-Action',
      phaseColor: '#008dcc',
      subtitle: 'Establishing Intent',
      patterns: [
        {
          icon: FileSearch,
          name: 'Intent Preview',
          description:
            'Agent shows what it plans to do before acting. Users see "Proceed," "Edit," or "Handle it Myself."',
        },
        {
          icon: SlidersHorizontal,
          name: 'Autonomy Dial',
          description:
            'Users set how much independence the agent gets. Progressive authorization, not binary on/off.',
        },
      ],
    },
    {
      phase: 'In-Action',
      phaseColor: '#8b5cf6',
      subtitle: 'Providing Context',
      patterns: [
        {
          icon: Brain,
          name: 'Explainable Rationale',
          description:
            'Agent surfaces the "why" and "how certain" while working. Transparency without overwhelm.',
        },
        {
          icon: Activity,
          name: 'Confidence Signal',
          description:
            'Visual indicators of certainty. Color-coded badges, percentages, or qualitative labels.',
        },
      ],
    },
    {
      phase: 'Post-Action',
      phaseColor: '#F90B8A',
      subtitle: 'Safety and Recovery',
      patterns: [
        {
          icon: RotateCcw,
          name: 'Action Audit & Undo',
          description:
            'Complete log of agent actions with reversal. Not just "undo" but a timeline of everything.',
        },
        {
          icon: ArrowUpRight,
          name: 'Escalation Pathway',
          description:
            'Clear routes for human handoff. Agent detects low confidence or high stakes and defers.',
        },
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F90B8A]/10 border border-[#F90B8A]/20 mb-3'>
            <Shield className='w-4 h-4' style={{ color: '#F90B8A' }} />
            <span
              className='text-sm font-semibold'
              style={{ color: '#F90B8A' }}
            >
              Industry Consensus
            </span>
          </div>
          <h3 className='text-xl font-bold text-foreground'>
            The Six Agentic UX Patterns
          </h3>
          <p className='text-muted-foreground text-sm mt-2'>
            From Smashing Magazine, UXmatters, and UX Magazine (2025-2026)
          </p>
        </div>

        <div className='space-y-6'>
          {phases.map((phase, phaseIdx) => (
            <div key={phase.phase}>
              <div className='flex items-center gap-3 mb-3'>
                <div
                  className='w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold'
                  style={{
                    backgroundColor: `${phase.phaseColor}15`,
                    color: phase.phaseColor,
                  }}
                >
                  {phaseIdx + 1}
                </div>
                <div>
                  <h4
                    className='font-bold text-base'
                    style={{ color: phase.phaseColor }}
                  >
                    {phase.phase}
                  </h4>
                  <p className='text-xs text-muted-foreground'>
                    {phase.subtitle}
                  </p>
                </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3 ml-11'>
                {phase.patterns.map(pattern => {
                  const Icon = pattern.icon
                  return (
                    <div
                      key={pattern.name}
                      className='bg-background border border-border/60 rounded-xl p-4'
                    >
                      <div className='flex items-center gap-2 mb-2'>
                        <Icon
                          className='w-4 h-4'
                          style={{ color: phase.phaseColor }}
                        />
                        <h5 className='font-semibold text-sm text-foreground'>
                          {pattern.name}
                        </h5>
                      </div>
                      <p className='text-xs text-muted-foreground leading-relaxed'>
                        {pattern.description}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <div className='mt-6 bg-[#FDC304]/5 border border-[#FDC304]/20 rounded-xl p-4'>
          <p className='text-sm text-foreground/80 text-center'>
            These are the new &quot;hamburger menu and infinite scroll&quot; of
            the agentic era. Every product shipping AI agents in 2026 needs
            these.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Protocol Stack Diagram: MCP, A2A, A2UI, AG-UI
 */
export function AgenticProtocolStackDiagram() {
  const protocols = [
    {
      name: 'AG-UI',
      fullName: 'Agent-User Interaction',
      color: '#F90B8A',
      purpose: 'How agent and UI communicate in real-time',
      creator: 'CopilotKit',
      adopters: 'Google, LangChain, AWS, Microsoft, Oracle',
      detail:
        'Streams ~16 event types over HTTP/SSE between agent backends and frontends',
    },
    {
      name: 'A2UI',
      fullName: 'Agent-to-User Interface',
      color: '#8b5cf6',
      purpose: 'What UI to render',
      creator: 'Google',
      adopters: 'Gemini Enterprise, Flutter GenUI, Opal',
      detail:
        'Declarative data format where agents request components from a pre-approved catalog',
    },
    {
      name: 'A2A',
      fullName: 'Agent-to-Agent',
      color: '#FDC304',
      purpose: 'Inter-agent communication',
      creator: 'Google',
      adopters: 'Multi-agent orchestration systems',
      detail: 'How specialized agents coordinate, delegate, and share context',
    },
    {
      name: 'MCP',
      fullName: 'Model Context Protocol',
      color: '#008dcc',
      purpose: 'Tools and data sources for agents',
      creator: 'Anthropic',
      adopters: 'ChatGPT, Claude, VS Code, Goose, Shopify',
      detail:
        'Standardizes how agents discover and use external tools and data',
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#008dcc]/10 border border-[#008dcc]/20 mb-3'>
            <Layers className='w-4 h-4' style={{ color: '#008dcc' }} />
            <span
              className='text-sm font-semibold'
              style={{ color: '#008dcc' }}
            >
              The 2026 Stack
            </span>
          </div>
          <h3 className='text-xl font-bold text-foreground'>
            Four Protocols Powering Agentic Interfaces
          </h3>
          <p className='text-muted-foreground text-sm mt-2'>
            From user-facing UI to agent-to-agent coordination
          </p>
        </div>

        <div className='space-y-3'>
          {protocols.map((proto, idx) => (
            <div
              key={proto.name}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-start gap-4'>
                <div className='flex-shrink-0'>
                  <div
                    className='w-14 h-14 rounded-lg flex items-center justify-center font-bold text-lg'
                    style={{
                      backgroundColor: `${proto.color}15`,
                      color: proto.color,
                    }}
                  >
                    {proto.name}
                  </div>
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='font-bold text-sm text-foreground'>
                      {proto.fullName}
                    </span>
                    <span
                      className='text-xs px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${proto.color}15`,
                        color: proto.color,
                      }}
                    >
                      {proto.creator}
                    </span>
                  </div>
                  <p className='text-xs text-foreground/80 mb-1'>
                    {proto.purpose}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {proto.detail}
                  </p>
                  {idx < protocols.length && (
                    <p className='text-xs text-muted-foreground mt-1'>
                      <span className='font-medium text-foreground/70'>
                        Adopters:
                      </span>{' '}
                      {proto.adopters}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='mt-4 flex items-center justify-center'>
          <div className='flex items-center gap-2 text-xs text-muted-foreground'>
            <Monitor className='w-3.5 h-3.5' />
            <span>User-Facing</span>
            <div className='w-16 h-px bg-[var(--border)]' />
            <span>Infrastructure</span>
            <Wrench className='w-3.5 h-3.5' />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Designer Role Evolution Spectrum
 */
export function DesignerEvolutionDiagram() {
  const roles = [
    {
      category: 'Very High Automation Risk',
      color: '#F90B8A',
      roles: ['UI Design', 'Motion Design', 'UX Writing', 'Generative Design'],
      note: 'Pattern libraries + AI generation = commoditized',
    },
    {
      category: 'Medium Risk',
      color: '#FDC304',
      roles: ['Human Factors', 'HCI Research', 'Ubiquitous Computing'],
      note: 'Augmented but not replaced',
    },
    {
      category: 'Low Risk (Hardest to Automate)',
      color: '#008dcc',
      roles: ['Cognitive Science', 'Psychology', 'Sociology'],
      note: 'Human understanding cannot be automated',
    },
  ]

  const emergingRoles = [
    {
      title: 'AI Orchestration Designer',
      icon: Network,
      description: 'Designing how multiple agents coordinate',
    },
    {
      title: 'Verification UX Specialist',
      icon: Shield,
      description: 'Designing how users verify AI outputs',
    },
    {
      title: 'Failure Mode Specialist',
      icon: AlertTriangle,
      description: 'Designing graceful degradation',
    },
    {
      title: 'Interaction Choreographer',
      icon: Users,
      description: 'Multi-step, mixed-initiative flows',
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FDC304]/10 border border-[#FDC304]/20 mb-3'>
            <Palette className='w-4 h-4' style={{ color: '#FDC304' }} />
            <span
              className='text-sm font-semibold'
              style={{ color: '#FDC304' }}
            >
              Career Impact
            </span>
          </div>
          <h3 className='text-xl font-bold text-foreground'>
            Design Specialization Automation Spectrum
          </h3>
          <p className='text-muted-foreground text-sm mt-2'>
            Based on Medeiros/Designative Design Specialization Impact Matrix
          </p>
        </div>

        <div className='space-y-4 mb-8'>
          {roles.map(tier => (
            <div key={tier.category}>
              <div className='flex items-center gap-2 mb-2'>
                <div
                  className='w-3 h-3 rounded-full'
                  style={{ backgroundColor: tier.color }}
                />
                <span
                  className='font-semibold text-sm'
                  style={{ color: tier.color }}
                >
                  {tier.category}
                </span>
              </div>
              <div className='ml-5'>
                <div className='flex flex-wrap gap-2 mb-1.5'>
                  {tier.roles.map(role => (
                    <span
                      key={role}
                      className='text-xs px-3 py-1 rounded-full font-medium'
                      style={{
                        backgroundColor: `${tier.color}15`,
                        color: tier.color,
                      }}
                    >
                      {role}
                    </span>
                  ))}
                </div>
                <p className='text-xs text-muted-foreground'>{tier.note}</p>
              </div>
            </div>
          ))}
        </div>

        <div className='bg-background border border-border/60 rounded-xl p-5'>
          <span className='font-bold text-sm text-foreground mb-4 flex items-center gap-2'>
            <TrendingUp className='w-4 h-4' style={{ color: '#008dcc' }} />
            Emerging Roles (2026+)
          </span>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            {emergingRoles.map(role => {
              const Icon = role.icon
              return (
                <div
                  key={role.title}
                  className='flex items-start gap-3 bg-muted rounded-lg p-3'
                >
                  <Icon
                    className='w-4 h-4 mt-0.5 flex-shrink-0'
                    style={{ color: '#008dcc' }}
                  />
                  <div>
                    <p className='text-sm font-medium text-foreground'>
                      {role.title}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {role.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Product Redesign Timeline
 */
export function ProductRedesignDiagram() {
  const products = [
    {
      name: 'Salesforce',
      change: 'Agent Canvas + Agent Script',
      thesis: '"AI agents will become the new UI"',
      color: '#008dcc',
      icon: Bot,
    },
    {
      name: 'Microsoft',
      change: 'Agents on Windows taskbar + Work IQ',
      thesis: 'Agent status as OS-level primitive',
      color: '#8b5cf6',
      icon: Monitor,
    },
    {
      name: 'Figma',
      change: 'Make (prompts to prototypes) + UI3',
      thesis: 'Conversational canvas replaces manual design',
      color: '#F90B8A',
      icon: Palette,
    },
    {
      name: 'Notion',
      change: '20-min autonomous agents + multi-model',
      thesis: 'AI as autonomous worker, not assistant',
      color: '#FDC304',
      icon: Brain,
    },
    {
      name: 'Adobe',
      change: 'Project Moonlight cross-app agent',
      thesis: 'Conversational layer above all tools',
      color: '#008dcc',
      icon: Layers,
    },
    {
      name: 'GitHub',
      change: 'Copilot Agent Mode + SKILL.md',
      thesis: 'From autocomplete to development partner',
      color: '#8b5cf6',
      icon: Wrench,
    },
    {
      name: 'Slack',
      change: 'Agentforce agents as teammates',
      thesis: 'AI appears in sidebar like a colleague',
      color: '#F90B8A',
      icon: MessageSquare,
    },
    {
      name: 'Apple',
      change: 'Campos: ground-up Siri redesign',
      thesis: 'Agent-first OS for iOS 27',
      color: '#FDC304',
      icon: Zap,
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#008dcc]/10 border border-[#008dcc]/20 mb-3'>
            <Bot className='w-4 h-4' style={{ color: '#008dcc' }} />
            <span
              className='text-sm font-semibold'
              style={{ color: '#008dcc' }}
            >
              2025-2026
            </span>
          </div>
          <h3 className='text-xl font-bold text-foreground'>
            Every Major Product Is Redesigning for Agents
          </h3>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          {products.map(product => {
            const Icon = product.icon
            return (
              <div
                key={product.name}
                className='bg-background border border-border/60 rounded-xl p-4'
              >
                <div className='flex items-center gap-2 mb-2'>
                  <div
                    className='w-7 h-7 rounded-md flex items-center justify-center'
                    style={{ backgroundColor: `${product.color}15` }}
                  >
                    <Icon
                      className='w-3.5 h-3.5'
                      style={{ color: product.color }}
                    />
                  </div>
                  <span className='font-bold text-sm text-foreground'>
                    {product.name}
                  </span>
                </div>
                <p className='text-xs text-foreground/80 mb-1'>
                  {product.change}
                </p>
                <p className='text-xs text-muted-foreground italic'>
                  {product.thesis}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
